// ──────────────────────────────────────────────────────────────
// Country data for the mobile CountryPicker component.
// Mirrors apps/backend/src/config/country-codes.js
// Sorted by dialCode length DESC for longest-prefix matching.
// ──────────────────────────────────────────────────────────────

export interface Country {
  dialCode: string;
  iso2: string;
  name: string;
  flag: string;
  timezone: string;
}

/** ISO codes of countries pinned to top of picker */
export const POPULAR_COUNTRY_CODES = ['LB', 'AE', 'SA', 'US', 'GB', 'FR'];

export const COUNTRIES: Country[] = [
  // === 4-digit dial codes ===
  { dialCode: '1242', iso2: 'BS', name: 'Bahamas',                flag: '🇧🇸', timezone: 'America/Nassau' },
  { dialCode: '1246', iso2: 'BB', name: 'Barbados',               flag: '🇧🇧', timezone: 'America/Barbados' },
  { dialCode: '1264', iso2: 'AI', name: 'Anguilla',               flag: '🇦🇮', timezone: 'America/Anguilla' },
  { dialCode: '1268', iso2: 'AG', name: 'Antigua & Barbuda',      flag: '🇦🇬', timezone: 'America/Antigua' },
  { dialCode: '1284', iso2: 'VG', name: 'British Virgin Islands', flag: '🇻🇬', timezone: 'America/Virgin' },
  { dialCode: '1340', iso2: 'VI', name: 'US Virgin Islands',      flag: '🇻🇮', timezone: 'America/Virgin' },
  { dialCode: '1345', iso2: 'KY', name: 'Cayman Islands',         flag: '🇰🇾', timezone: 'America/Cayman' },
  { dialCode: '1441', iso2: 'BM', name: 'Bermuda',                flag: '🇧🇲', timezone: 'Atlantic/Bermuda' },
  { dialCode: '1473', iso2: 'GD', name: 'Grenada',                flag: '🇬🇩', timezone: 'America/Grenada' },
  { dialCode: '1649', iso2: 'TC', name: 'Turks & Caicos',         flag: '🇹🇨', timezone: 'America/Grand_Turk' },
  { dialCode: '1664', iso2: 'MS', name: 'Montserrat',             flag: '🇲🇸', timezone: 'America/Montserrat' },
  { dialCode: '1670', iso2: 'MP', name: 'Northern Mariana Islands',flag: '🇲🇵', timezone: 'Pacific/Guam' },
  { dialCode: '1671', iso2: 'GU', name: 'Guam',                   flag: '🇬🇺', timezone: 'Pacific/Guam' },
  { dialCode: '1684', iso2: 'AS', name: 'American Samoa',         flag: '🇦🇸', timezone: 'Pacific/Pago_Pago' },
  { dialCode: '1721', iso2: 'SX', name: 'Sint Maarten',           flag: '🇸🇽', timezone: 'America/Lower_Princes' },
  { dialCode: '1758', iso2: 'LC', name: 'Saint Lucia',            flag: '🇱🇨', timezone: 'America/St_Lucia' },
  { dialCode: '1767', iso2: 'DM', name: 'Dominica',               flag: '🇩🇲', timezone: 'America/Dominica' },
  { dialCode: '1784', iso2: 'VC', name: 'St Vincent & Grenadines',flag: '🇻🇨', timezone: 'America/St_Vincent' },
  { dialCode: '1787', iso2: 'PR', name: 'Puerto Rico',            flag: '🇵🇷', timezone: 'America/Puerto_Rico' },
  { dialCode: '1809', iso2: 'DO', name: 'Dominican Republic',     flag: '🇩🇴', timezone: 'America/Santo_Domingo' },
  { dialCode: '1868', iso2: 'TT', name: 'Trinidad & Tobago',      flag: '🇹🇹', timezone: 'America/Port_of_Spain' },
  { dialCode: '1869', iso2: 'KN', name: 'Saint Kitts & Nevis',    flag: '🇰🇳', timezone: 'America/St_Kitts' },
  { dialCode: '1876', iso2: 'JM', name: 'Jamaica',                flag: '🇯🇲', timezone: 'America/Jamaica' },

  // === 3-digit dial codes ===
  { dialCode: '212', iso2: 'MA', name: 'Morocco',         flag: '🇲🇦', timezone: 'Africa/Casablanca' },
  { dialCode: '213', iso2: 'DZ', name: 'Algeria',         flag: '🇩🇿', timezone: 'Africa/Algiers' },
  { dialCode: '216', iso2: 'TN', name: 'Tunisia',         flag: '🇹🇳', timezone: 'Africa/Tunis' },
  { dialCode: '218', iso2: 'LY', name: 'Libya',           flag: '🇱🇾', timezone: 'Africa/Tripoli' },
  { dialCode: '220', iso2: 'GM', name: 'Gambia',          flag: '🇬🇲', timezone: 'Africa/Banjul' },
  { dialCode: '221', iso2: 'SN', name: 'Senegal',         flag: '🇸🇳', timezone: 'Africa/Dakar' },
  { dialCode: '222', iso2: 'MR', name: 'Mauritania',      flag: '🇲🇷', timezone: 'Africa/Nouakchott' },
  { dialCode: '223', iso2: 'ML', name: 'Mali',            flag: '🇲🇱', timezone: 'Africa/Bamako' },
  { dialCode: '224', iso2: 'GN', name: 'Guinea',          flag: '🇬🇳', timezone: 'Africa/Conakry' },
  { dialCode: '225', iso2: 'CI', name: 'Ivory Coast',     flag: '🇨🇮', timezone: 'Africa/Abidjan' },
  { dialCode: '226', iso2: 'BF', name: 'Burkina Faso',    flag: '🇧🇫', timezone: 'Africa/Ouagadougou' },
  { dialCode: '227', iso2: 'NE', name: 'Niger',           flag: '🇳🇪', timezone: 'Africa/Niamey' },
  { dialCode: '228', iso2: 'TG', name: 'Togo',            flag: '🇹🇬', timezone: 'Africa/Lome' },
  { dialCode: '229', iso2: 'BJ', name: 'Benin',           flag: '🇧🇯', timezone: 'Africa/Porto-Novo' },
  { dialCode: '230', iso2: 'MU', name: 'Mauritius',       flag: '🇲🇺', timezone: 'Indian/Mauritius' },
  { dialCode: '231', iso2: 'LR', name: 'Liberia',         flag: '🇱🇷', timezone: 'Africa/Monrovia' },
  { dialCode: '232', iso2: 'SL', name: 'Sierra Leone',    flag: '🇸🇱', timezone: 'Africa/Freetown' },
  { dialCode: '233', iso2: 'GH', name: 'Ghana',           flag: '🇬🇭', timezone: 'Africa/Accra' },
  { dialCode: '234', iso2: 'NG', name: 'Nigeria',         flag: '🇳🇬', timezone: 'Africa/Lagos' },
  { dialCode: '235', iso2: 'TD', name: 'Chad',            flag: '🇹🇩', timezone: 'Africa/Ndjamena' },
  { dialCode: '236', iso2: 'CF', name: 'Central African Republic', flag: '🇨🇫', timezone: 'Africa/Bangui' },
  { dialCode: '237', iso2: 'CM', name: 'Cameroon',        flag: '🇨🇲', timezone: 'Africa/Douala' },
  { dialCode: '238', iso2: 'CV', name: 'Cape Verde',      flag: '🇨🇻', timezone: 'Atlantic/Cape_Verde' },
  { dialCode: '239', iso2: 'ST', name: 'São Tomé & Príncipe', flag: '🇸🇹', timezone: 'Africa/Sao_Tome' },
  { dialCode: '240', iso2: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶', timezone: 'Africa/Malabo' },
  { dialCode: '241', iso2: 'GA', name: 'Gabon',           flag: '🇬🇦', timezone: 'Africa/Libreville' },
  { dialCode: '242', iso2: 'CG', name: 'Congo',           flag: '🇨🇬', timezone: 'Africa/Brazzaville' },
  { dialCode: '243', iso2: 'CD', name: 'DR Congo',        flag: '🇨🇩', timezone: 'Africa/Kinshasa' },
  { dialCode: '244', iso2: 'AO', name: 'Angola',          flag: '🇦🇴', timezone: 'Africa/Luanda' },
  { dialCode: '245', iso2: 'GW', name: 'Guinea-Bissau',   flag: '🇬🇼', timezone: 'Africa/Bissau' },
  { dialCode: '246', iso2: 'IO', name: 'British Indian Ocean', flag: '🇮🇴', timezone: 'Indian/Chagos' },
  { dialCode: '248', iso2: 'SC', name: 'Seychelles',      flag: '🇸🇨', timezone: 'Indian/Mahe' },
  { dialCode: '249', iso2: 'SD', name: 'Sudan',           flag: '🇸🇩', timezone: 'Africa/Khartoum' },
  { dialCode: '250', iso2: 'RW', name: 'Rwanda',          flag: '🇷🇼', timezone: 'Africa/Kigali' },
  { dialCode: '251', iso2: 'ET', name: 'Ethiopia',        flag: '🇪🇹', timezone: 'Africa/Addis_Ababa' },
  { dialCode: '252', iso2: 'SO', name: 'Somalia',         flag: '🇸🇴', timezone: 'Africa/Mogadishu' },
  { dialCode: '253', iso2: 'DJ', name: 'Djibouti',        flag: '🇩🇯', timezone: 'Africa/Djibouti' },
  { dialCode: '254', iso2: 'KE', name: 'Kenya',           flag: '🇰🇪', timezone: 'Africa/Nairobi' },
  { dialCode: '255', iso2: 'TZ', name: 'Tanzania',        flag: '🇹🇿', timezone: 'Africa/Dar_es_Salaam' },
  { dialCode: '256', iso2: 'UG', name: 'Uganda',          flag: '🇺🇬', timezone: 'Africa/Kampala' },
  { dialCode: '257', iso2: 'BI', name: 'Burundi',         flag: '🇧🇮', timezone: 'Africa/Bujumbura' },
  { dialCode: '258', iso2: 'MZ', name: 'Mozambique',      flag: '🇲🇿', timezone: 'Africa/Maputo' },
  { dialCode: '260', iso2: 'ZM', name: 'Zambia',          flag: '🇿🇲', timezone: 'Africa/Lusaka' },
  { dialCode: '261', iso2: 'MG', name: 'Madagascar',      flag: '🇲🇬', timezone: 'Indian/Antananarivo' },
  { dialCode: '262', iso2: 'RE', name: 'Réunion',         flag: '🇷🇪', timezone: 'Indian/Reunion' },
  { dialCode: '263', iso2: 'ZW', name: 'Zimbabwe',        flag: '🇿🇼', timezone: 'Africa/Harare' },
  { dialCode: '264', iso2: 'NA', name: 'Namibia',         flag: '🇳🇦', timezone: 'Africa/Windhoek' },
  { dialCode: '265', iso2: 'MW', name: 'Malawi',          flag: '🇲🇼', timezone: 'Africa/Blantyre' },
  { dialCode: '266', iso2: 'LS', name: 'Lesotho',         flag: '🇱🇸', timezone: 'Africa/Maseru' },
  { dialCode: '267', iso2: 'BW', name: 'Botswana',        flag: '🇧🇼', timezone: 'Africa/Gaborone' },
  { dialCode: '268', iso2: 'SZ', name: 'Eswatini',        flag: '🇸🇿', timezone: 'Africa/Mbabane' },
  { dialCode: '269', iso2: 'KM', name: 'Comoros',         flag: '🇰🇲', timezone: 'Indian/Comoro' },
  { dialCode: '290', iso2: 'SH', name: 'Saint Helena',    flag: '🇸🇭', timezone: 'Atlantic/St_Helena' },
  { dialCode: '291', iso2: 'ER', name: 'Eritrea',         flag: '🇪🇷', timezone: 'Africa/Asmara' },
  { dialCode: '297', iso2: 'AW', name: 'Aruba',           flag: '🇦🇼', timezone: 'America/Aruba' },
  { dialCode: '298', iso2: 'FO', name: 'Faroe Islands',   flag: '🇫🇴', timezone: 'Atlantic/Faroe' },
  { dialCode: '299', iso2: 'GL', name: 'Greenland',       flag: '🇬🇱', timezone: 'America/Godthab' },
  { dialCode: '350', iso2: 'GI', name: 'Gibraltar',       flag: '🇬🇮', timezone: 'Europe/Gibraltar' },
  { dialCode: '351', iso2: 'PT', name: 'Portugal',        flag: '🇵🇹', timezone: 'Europe/Lisbon' },
  { dialCode: '352', iso2: 'LU', name: 'Luxembourg',      flag: '🇱🇺', timezone: 'Europe/Luxembourg' },
  { dialCode: '353', iso2: 'IE', name: 'Ireland',         flag: '🇮🇪', timezone: 'Europe/Dublin' },
  { dialCode: '354', iso2: 'IS', name: 'Iceland',         flag: '🇮🇸', timezone: 'Atlantic/Reykjavik' },
  { dialCode: '355', iso2: 'AL', name: 'Albania',         flag: '🇦🇱', timezone: 'Europe/Tirane' },
  { dialCode: '356', iso2: 'MT', name: 'Malta',           flag: '🇲🇹', timezone: 'Europe/Malta' },
  { dialCode: '357', iso2: 'CY', name: 'Cyprus',          flag: '🇨🇾', timezone: 'Asia/Nicosia' },
  { dialCode: '358', iso2: 'FI', name: 'Finland',         flag: '🇫🇮', timezone: 'Europe/Helsinki' },
  { dialCode: '359', iso2: 'BG', name: 'Bulgaria',        flag: '🇧🇬', timezone: 'Europe/Sofia' },
  { dialCode: '370', iso2: 'LT', name: 'Lithuania',       flag: '🇱🇹', timezone: 'Europe/Vilnius' },
  { dialCode: '371', iso2: 'LV', name: 'Latvia',          flag: '🇱🇻', timezone: 'Europe/Riga' },
  { dialCode: '372', iso2: 'EE', name: 'Estonia',         flag: '🇪🇪', timezone: 'Europe/Tallinn' },
  { dialCode: '373', iso2: 'MD', name: 'Moldova',         flag: '🇲🇩', timezone: 'Europe/Chisinau' },
  { dialCode: '374', iso2: 'AM', name: 'Armenia',         flag: '🇦🇲', timezone: 'Asia/Yerevan' },
  { dialCode: '375', iso2: 'BY', name: 'Belarus',         flag: '🇧🇾', timezone: 'Europe/Minsk' },
  { dialCode: '376', iso2: 'AD', name: 'Andorra',         flag: '🇦🇩', timezone: 'Europe/Andorra' },
  { dialCode: '377', iso2: 'MC', name: 'Monaco',          flag: '🇲🇨', timezone: 'Europe/Monaco' },
  { dialCode: '378', iso2: 'SM', name: 'San Marino',      flag: '🇸🇲', timezone: 'Europe/San_Marino' },
  { dialCode: '380', iso2: 'UA', name: 'Ukraine',         flag: '🇺🇦', timezone: 'Europe/Kyiv' },
  { dialCode: '381', iso2: 'RS', name: 'Serbia',          flag: '🇷🇸', timezone: 'Europe/Belgrade' },
  { dialCode: '382', iso2: 'ME', name: 'Montenegro',      flag: '🇲🇪', timezone: 'Europe/Podgorica' },
  { dialCode: '383', iso2: 'XK', name: 'Kosovo',          flag: '🇽🇰', timezone: 'Europe/Belgrade' },
  { dialCode: '385', iso2: 'HR', name: 'Croatia',         flag: '🇭🇷', timezone: 'Europe/Zagreb' },
  { dialCode: '386', iso2: 'SI', name: 'Slovenia',        flag: '🇸🇮', timezone: 'Europe/Ljubljana' },
  { dialCode: '387', iso2: 'BA', name: 'Bosnia & Herzegovina', flag: '🇧🇦', timezone: 'Europe/Sarajevo' },
  { dialCode: '389', iso2: 'MK', name: 'North Macedonia', flag: '🇲🇰', timezone: 'Europe/Skopje' },
  { dialCode: '420', iso2: 'CZ', name: 'Czech Republic',  flag: '🇨🇿', timezone: 'Europe/Prague' },
  { dialCode: '421', iso2: 'SK', name: 'Slovakia',        flag: '🇸🇰', timezone: 'Europe/Bratislava' },
  { dialCode: '423', iso2: 'LI', name: 'Liechtenstein',   flag: '🇱🇮', timezone: 'Europe/Vaduz' },
  { dialCode: '500', iso2: 'FK', name: 'Falkland Islands', flag: '🇫🇰', timezone: 'Atlantic/Stanley' },
  { dialCode: '501', iso2: 'BZ', name: 'Belize',          flag: '🇧🇿', timezone: 'America/Belize' },
  { dialCode: '502', iso2: 'GT', name: 'Guatemala',       flag: '🇬🇹', timezone: 'America/Guatemala' },
  { dialCode: '503', iso2: 'SV', name: 'El Salvador',     flag: '🇪🇸', timezone: 'America/El_Salvador' },
  { dialCode: '504', iso2: 'HN', name: 'Honduras',        flag: '🇭🇳', timezone: 'America/Tegucigalpa' },
  { dialCode: '505', iso2: 'NI', name: 'Nicaragua',       flag: '🇳🇮', timezone: 'America/Managua' },
  { dialCode: '506', iso2: 'CR', name: 'Costa Rica',      flag: '🇨🇷', timezone: 'America/Costa_Rica' },
  { dialCode: '507', iso2: 'PA', name: 'Panama',          flag: '🇵🇦', timezone: 'America/Panama' },
  { dialCode: '509', iso2: 'HT', name: 'Haiti',           flag: '🇭🇹', timezone: 'America/Port-au-Prince' },
  { dialCode: '590', iso2: 'GP', name: 'Guadeloupe',      flag: '🇬🇵', timezone: 'America/Guadeloupe' },
  { dialCode: '591', iso2: 'BO', name: 'Bolivia',         flag: '🇧🇴', timezone: 'America/La_Paz' },
  { dialCode: '592', iso2: 'GY', name: 'Guyana',          flag: '🇬🇾', timezone: 'America/Guyana' },
  { dialCode: '593', iso2: 'EC', name: 'Ecuador',         flag: '🇪🇨', timezone: 'America/Guayaquil' },
  { dialCode: '594', iso2: 'GF', name: 'French Guiana',   flag: '🇬🇫', timezone: 'America/Cayenne' },
  { dialCode: '595', iso2: 'PY', name: 'Paraguay',        flag: '🇵🇾', timezone: 'America/Asuncion' },
  { dialCode: '596', iso2: 'MQ', name: 'Martinique',      flag: '🇲🇶', timezone: 'America/Martinique' },
  { dialCode: '597', iso2: 'SR', name: 'Suriname',        flag: '🇸🇷', timezone: 'America/Paramaribo' },
  { dialCode: '598', iso2: 'UY', name: 'Uruguay',         flag: '🇺🇾', timezone: 'America/Montevideo' },
  { dialCode: '599', iso2: 'CW', name: 'Curaçao',         flag: '🇨🇼', timezone: 'America/Curacao' },
  { dialCode: '670', iso2: 'TL', name: 'Timor-Leste',     flag: '🇹🇱', timezone: 'Asia/Dili' },
  { dialCode: '672', iso2: 'NF', name: 'Norfolk Island',  flag: '🇳🇫', timezone: 'Pacific/Norfolk' },
  { dialCode: '673', iso2: 'BN', name: 'Brunei',          flag: '🇧🇳', timezone: 'Asia/Brunei' },
  { dialCode: '674', iso2: 'NR', name: 'Nauru',           flag: '🇳🇷', timezone: 'Pacific/Nauru' },
  { dialCode: '675', iso2: 'PG', name: 'Papua New Guinea', flag: '🇵🇬', timezone: 'Pacific/Port_Moresby' },
  { dialCode: '676', iso2: 'TO', name: 'Tonga',           flag: '🇹🇴', timezone: 'Pacific/Tongatapu' },
  { dialCode: '677', iso2: 'SB', name: 'Solomon Islands', flag: '🇸🇧', timezone: 'Pacific/Guadalcanal' },
  { dialCode: '678', iso2: 'VU', name: 'Vanuatu',         flag: '🇻🇺', timezone: 'Pacific/Efate' },
  { dialCode: '679', iso2: 'FJ', name: 'Fiji',            flag: '🇫🇯', timezone: 'Pacific/Fiji' },
  { dialCode: '680', iso2: 'PW', name: 'Palau',           flag: '🇵🇼', timezone: 'Pacific/Palau' },
  { dialCode: '685', iso2: 'WS', name: 'Samoa',           flag: '🇼🇸', timezone: 'Pacific/Apia' },
  { dialCode: '686', iso2: 'KI', name: 'Kiribati',        flag: '🇰🇮', timezone: 'Pacific/Tarawa' },
  { dialCode: '687', iso2: 'NC', name: 'New Caledonia',   flag: '🇳🇨', timezone: 'Pacific/Noumea' },
  { dialCode: '689', iso2: 'PF', name: 'French Polynesia', flag: '🇵🇫', timezone: 'Pacific/Tahiti' },
  { dialCode: '690', iso2: 'TK', name: 'Tokelau',         flag: '🇹🇰', timezone: 'Pacific/Fakaofo' },
  { dialCode: '691', iso2: 'FM', name: 'Micronesia',      flag: '🇫🇲', timezone: 'Pacific/Chuuk' },
  { dialCode: '692', iso2: 'MH', name: 'Marshall Islands', flag: '🇲🇭', timezone: 'Pacific/Majuro' },
  { dialCode: '850', iso2: 'KP', name: 'North Korea',     flag: '🇰🇵', timezone: 'Asia/Pyongyang' },
  { dialCode: '852', iso2: 'HK', name: 'Hong Kong',       flag: '🇭🇰', timezone: 'Asia/Hong_Kong' },
  { dialCode: '853', iso2: 'MO', name: 'Macau',           flag: '🇲🇴', timezone: 'Asia/Macau' },
  { dialCode: '855', iso2: 'KH', name: 'Cambodia',        flag: '🇰🇭', timezone: 'Asia/Phnom_Penh' },
  { dialCode: '856', iso2: 'LA', name: 'Laos',            flag: '🇱🇦', timezone: 'Asia/Vientiane' },
  { dialCode: '880', iso2: 'BD', name: 'Bangladesh',      flag: '🇧🇩', timezone: 'Asia/Dhaka' },
  { dialCode: '886', iso2: 'TW', name: 'Taiwan',          flag: '🇹🇼', timezone: 'Asia/Taipei' },
  { dialCode: '960', iso2: 'MV', name: 'Maldives',        flag: '🇲🇻', timezone: 'Indian/Maldives' },
  { dialCode: '961', iso2: 'LB', name: 'Lebanon',         flag: '🇱🇧', timezone: 'Asia/Beirut' },
  { dialCode: '962', iso2: 'JO', name: 'Jordan',          flag: '🇯🇴', timezone: 'Asia/Amman' },
  { dialCode: '963', iso2: 'SY', name: 'Syria',           flag: '🇸🇾', timezone: 'Asia/Damascus' },
  { dialCode: '964', iso2: 'IQ', name: 'Iraq',            flag: '🇮🇶', timezone: 'Asia/Baghdad' },
  { dialCode: '965', iso2: 'KW', name: 'Kuwait',          flag: '🇰🇼', timezone: 'Asia/Kuwait' },
  { dialCode: '966', iso2: 'SA', name: 'Saudi Arabia',    flag: '🇸🇦', timezone: 'Asia/Riyadh' },
  { dialCode: '967', iso2: 'YE', name: 'Yemen',           flag: '🇾🇪', timezone: 'Asia/Aden' },
  { dialCode: '968', iso2: 'OM', name: 'Oman',            flag: '🇴🇲', timezone: 'Asia/Muscat' },
  { dialCode: '970', iso2: 'PS', name: 'Palestine',       flag: '🇵🇸', timezone: 'Asia/Hebron' },
  { dialCode: '971', iso2: 'AE', name: 'UAE',             flag: '🇦🇪', timezone: 'Asia/Dubai' },
  { dialCode: '972', iso2: 'IL', name: 'Israel',          flag: '🇮🇱', timezone: 'Asia/Jerusalem' },
  { dialCode: '973', iso2: 'BH', name: 'Bahrain',         flag: '🇧🇭', timezone: 'Asia/Bahrain' },
  { dialCode: '974', iso2: 'QA', name: 'Qatar',           flag: '🇶🇦', timezone: 'Asia/Qatar' },
  { dialCode: '975', iso2: 'BT', name: 'Bhutan',          flag: '🇧🇹', timezone: 'Asia/Thimphu' },
  { dialCode: '976', iso2: 'MN', name: 'Mongolia',        flag: '🇲🇳', timezone: 'Asia/Ulaanbaatar' },
  { dialCode: '977', iso2: 'NP', name: 'Nepal',           flag: '🇳🇵', timezone: 'Asia/Kathmandu' },
  { dialCode: '992', iso2: 'TJ', name: 'Tajikistan',      flag: '🇹🇯', timezone: 'Asia/Dushanbe' },
  { dialCode: '993', iso2: 'TM', name: 'Turkmenistan',    flag: '🇹🇲', timezone: 'Asia/Ashgabat' },
  { dialCode: '994', iso2: 'AZ', name: 'Azerbaijan',      flag: '🇦🇿', timezone: 'Asia/Baku' },
  { dialCode: '995', iso2: 'GE', name: 'Georgia',         flag: '🇬🇪', timezone: 'Asia/Tbilisi' },
  { dialCode: '996', iso2: 'KG', name: 'Kyrgyzstan',      flag: '🇰🇬', timezone: 'Asia/Bishkek' },
  { dialCode: '998', iso2: 'UZ', name: 'Uzbekistan',      flag: '🇺🇿', timezone: 'Asia/Tashkent' },

  // === 2-digit dial codes ===
  { dialCode: '20', iso2: 'EG', name: 'Egypt',            flag: '🇪🇬', timezone: 'Africa/Cairo' },
  { dialCode: '27', iso2: 'ZA', name: 'South Africa',     flag: '🇿🇦', timezone: 'Africa/Johannesburg' },
  { dialCode: '30', iso2: 'GR', name: 'Greece',           flag: '🇬🇷', timezone: 'Europe/Athens' },
  { dialCode: '31', iso2: 'NL', name: 'Netherlands',      flag: '🇳🇱', timezone: 'Europe/Amsterdam' },
  { dialCode: '32', iso2: 'BE', name: 'Belgium',          flag: '🇧🇪', timezone: 'Europe/Brussels' },
  { dialCode: '33', iso2: 'FR', name: 'France',           flag: '🇫🇷', timezone: 'Europe/Paris' },
  { dialCode: '34', iso2: 'ES', name: 'Spain',            flag: '🇪🇸', timezone: 'Europe/Madrid' },
  { dialCode: '36', iso2: 'HU', name: 'Hungary',          flag: '🇭🇺', timezone: 'Europe/Budapest' },
  { dialCode: '39', iso2: 'IT', name: 'Italy',            flag: '🇮🇹', timezone: 'Europe/Rome' },
  { dialCode: '40', iso2: 'RO', name: 'Romania',          flag: '🇷🇴', timezone: 'Europe/Bucharest' },
  { dialCode: '41', iso2: 'CH', name: 'Switzerland',      flag: '🇨🇭', timezone: 'Europe/Zurich' },
  { dialCode: '43', iso2: 'AT', name: 'Austria',          flag: '🇦🇹', timezone: 'Europe/Vienna' },
  { dialCode: '44', iso2: 'GB', name: 'United Kingdom',   flag: '🇬🇧', timezone: 'Europe/London' },
  { dialCode: '45', iso2: 'DK', name: 'Denmark',          flag: '🇩🇰', timezone: 'Europe/Copenhagen' },
  { dialCode: '46', iso2: 'SE', name: 'Sweden',           flag: '🇸🇪', timezone: 'Europe/Stockholm' },
  { dialCode: '47', iso2: 'NO', name: 'Norway',           flag: '🇳🇴', timezone: 'Europe/Oslo' },
  { dialCode: '48', iso2: 'PL', name: 'Poland',           flag: '🇵🇱', timezone: 'Europe/Warsaw' },
  { dialCode: '49', iso2: 'DE', name: 'Germany',          flag: '🇩🇪', timezone: 'Europe/Berlin' },
  { dialCode: '51', iso2: 'PE', name: 'Peru',             flag: '🇵🇪', timezone: 'America/Lima' },
  { dialCode: '52', iso2: 'MX', name: 'Mexico',           flag: '🇲🇽', timezone: 'America/Mexico_City' },
  { dialCode: '53', iso2: 'CU', name: 'Cuba',             flag: '🇨🇺', timezone: 'America/Havana' },
  { dialCode: '54', iso2: 'AR', name: 'Argentina',        flag: '🇦🇷', timezone: 'America/Argentina/Buenos_Aires' },
  { dialCode: '55', iso2: 'BR', name: 'Brazil',           flag: '🇧🇷', timezone: 'America/Sao_Paulo' },
  { dialCode: '56', iso2: 'CL', name: 'Chile',            flag: '🇨🇱', timezone: 'America/Santiago' },
  { dialCode: '57', iso2: 'CO', name: 'Colombia',         flag: '🇨🇴', timezone: 'America/Bogota' },
  { dialCode: '58', iso2: 'VE', name: 'Venezuela',        flag: '🇻🇪', timezone: 'America/Caracas' },
  { dialCode: '60', iso2: 'MY', name: 'Malaysia',         flag: '🇲🇾', timezone: 'Asia/Kuala_Lumpur' },
  { dialCode: '61', iso2: 'AU', name: 'Australia',        flag: '🇦🇺', timezone: 'Australia/Sydney' },
  { dialCode: '62', iso2: 'ID', name: 'Indonesia',        flag: '🇮🇩', timezone: 'Asia/Jakarta' },
  { dialCode: '63', iso2: 'PH', name: 'Philippines',      flag: '🇵🇭', timezone: 'Asia/Manila' },
  { dialCode: '64', iso2: 'NZ', name: 'New Zealand',      flag: '🇳🇿', timezone: 'Pacific/Auckland' },
  { dialCode: '65', iso2: 'SG', name: 'Singapore',        flag: '🇸🇬', timezone: 'Asia/Singapore' },
  { dialCode: '66', iso2: 'TH', name: 'Thailand',         flag: '🇹🇭', timezone: 'Asia/Bangkok' },
  { dialCode: '81', iso2: 'JP', name: 'Japan',            flag: '🇯🇵', timezone: 'Asia/Tokyo' },
  { dialCode: '82', iso2: 'KR', name: 'South Korea',      flag: '🇰🇷', timezone: 'Asia/Seoul' },
  { dialCode: '84', iso2: 'VN', name: 'Vietnam',          flag: '🇻🇳', timezone: 'Asia/Ho_Chi_Minh' },
  { dialCode: '86', iso2: 'CN', name: 'China',            flag: '🇨🇳', timezone: 'Asia/Shanghai' },
  { dialCode: '90', iso2: 'TR', name: 'Turkey',           flag: '🇹🇷', timezone: 'Europe/Istanbul' },
  { dialCode: '91', iso2: 'IN', name: 'India',            flag: '🇮🇳', timezone: 'Asia/Kolkata' },
  { dialCode: '92', iso2: 'PK', name: 'Pakistan',         flag: '🇵🇰', timezone: 'Asia/Karachi' },
  { dialCode: '93', iso2: 'AF', name: 'Afghanistan',      flag: '🇦🇫', timezone: 'Asia/Kabul' },
  { dialCode: '94', iso2: 'LK', name: 'Sri Lanka',        flag: '🇱🇰', timezone: 'Asia/Colombo' },
  { dialCode: '95', iso2: 'MM', name: 'Myanmar',          flag: '🇲🇲', timezone: 'Asia/Yangon' },
  { dialCode: '98', iso2: 'IR', name: 'Iran',             flag: '🇮🇷', timezone: 'Asia/Tehran' },

  // === 1-digit dial codes ===
  { dialCode: '1',  iso2: 'US', name: 'United States',    flag: '🇺🇸', timezone: 'America/New_York' },
  { dialCode: '7',  iso2: 'RU', name: 'Russia',           flag: '🇷🇺', timezone: 'Europe/Moscow' },
];

// Sorted index for longest-prefix matching
const _sortedCodes = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);

function digitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

function normalizeIntlPrefix(value: string): string {
  return String(value || '')
    .trim()
    .replace(/^00/, '+')
    .replace(/[^\d+]/g, '')
    .replace(/(?!^)\+/g, '');
}

/** Find the country whose dial code is the longest prefix of a phone number. */
export function getCountryByDialCode(phone: string): Country | null {
  const digits = digitsOnly(phone);
  for (const c of _sortedCodes) {
    if (digits.startsWith(c.dialCode)) return c;
  }
  return null;
}

export function parsePhoneDraft(phone: string, fallbackCountry: Country): {
  country: Country;
  localNumber: string;
  hadExplicitCountryCode: boolean;
} {
  const normalized = normalizeIntlPrefix(phone);
  const digits = digitsOnly(normalized);

  if (!digits) {
    return {
      country: fallbackCountry,
      localNumber: '',
      hadExplicitCountryCode: false,
    };
  }

  if (normalized.startsWith('+')) {
    const matchedCountry = getCountryByDialCode(digits);
    if (matchedCountry) {
      return {
        country: matchedCountry,
        localNumber: digits.slice(matchedCountry.dialCode.length),
        hadExplicitCountryCode: true,
      };
    }
  }

  // Users sometimes paste the full number without the leading +. Only auto-strip
  // the selected country's dial code for multi-digit prefixes to avoid breaking
  // local numbers in 1-digit dial-code countries like the US.
  if (
    fallbackCountry.dialCode.length > 1 &&
    digits.startsWith(fallbackCountry.dialCode) &&
    digits.length > fallbackCountry.dialCode.length + 4 &&
    !normalized.startsWith('0')
  ) {
    return {
      country: fallbackCountry,
      localNumber: digits.slice(fallbackCountry.dialCode.length),
      hadExplicitCountryCode: true,
    };
  }

  return {
    country: fallbackCountry,
    localNumber: digits,
    hadExplicitCountryCode: false,
  };
}

export function buildE164Phone(localNumber: string, country: Country): string {
  const localDigits = digitsOnly(localNumber).replace(/^0/, '');
  return `+${country.dialCode}${localDigits}`;
}

/** Get IANA timezone for a phone number. Defaults to 'Asia/Beirut'. */
export function getTimezoneForPhone(phone: string): string {
  return getCountryByDialCode(phone)?.timezone || 'Asia/Beirut';
}

/** Find a country by ISO2 code. */
export function getCountryByIso2(iso2: string): Country | null {
  return COUNTRIES.find((c) => c.iso2 === iso2) || null;
}
